import styled, { keyframes } from 'styled-components';

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  33% {
    transform: translateY(-15px) rotate(120deg);
  }
  66% {
    transform: translateY(8px) rotate(240deg);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.7);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 0 0 10px rgba(102, 126, 234, 0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

// Main Container
export const DashboardContainer = styled.div`
  min-height: calc(100vh - 80px);
  background: #ffffff;
  position: relative;
  overflow-x: hidden;
`;

// Floating Elements
export const FloatingElement = styled.div`
  position: absolute;
  width: 80px;
  height: 80px;
  background: linear-gradient(45deg, rgba(43, 56, 87, 0.08), rgba(197, 51, 123, 0.05));
  border-radius: 50%;
  animation: ${float} 20s ease-in-out infinite;
  animation-delay: ${props => props.delay};
  z-index: 1;
  
  &:nth-child(2) {
    top: 20%;
    right: 10%;
    width: 60px;
    height: 60px;
  }
  
  &:nth-child(3) {
    top: 60%;
    left: 5%;
    width: 100px;
    height: 100px;
  }
  
  &:nth-child(4) {
    bottom: 20%;
    right: 20%;
    width: 70px;
    height: 70px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

// Main Content
export const MainContent = styled.main`
  padding: 2rem;
  position: relative;
  z-index: 5;
  max-width: 1200px;
  margin: 0 auto;
  animation: ${fadeInUp} 1s ease-out 0.3s both;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

// Welcome Section
export const WelcomeSection = styled.section`
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(135deg, rgba(43, 56, 87, 0.05), rgba(255, 255, 255, 0.8));
  backdrop-filter: blur(20px);
  border: 1px solid rgba(43, 56, 87, 0.1);
  border-radius: 20px;
  padding: 3rem 2rem;
  animation: ${fadeInUp} 1s ease-out 0.5s both;

  @media (max-width: 768px) {
    margin-bottom: 2rem;
    padding: 2rem 1rem;
    border-radius: 15px;
  }
`;

export const WelcomeTitle = styled.h2`
  color: #2B3857;
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 1rem 0;
  text-shadow: 0 2px 4px rgba(43, 56, 87, 0.2);

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

export const WelcomeText = styled.p`
  color: #C5337B;
  font-size: 1.1rem;
  line-height: 1.6;
  margin: 0;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

// Cards Grid
export const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  animation: ${fadeInUp} 1s ease-out 0.7s both;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

export const Card = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(43, 56, 87, 0.1);
  border-radius: 16px;
  padding: 2rem;
  transition: all 0.4s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  animation: ${fadeInUp} 0.6s ease-out ${props => props.delay} both;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(43, 56, 87, 0.05),
      transparent
    );
    transition: left 0.6s;
  }

  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 40px rgba(43, 56, 87, 0.15);
    border-color: ${props => props.color}40;
    
    &:before {
      left: 100%;
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    
    &:hover {
      transform: translateY(-4px) scale(1.01);
    }
  }
`;

export const CardIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${props => props.color};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-bottom: 1.5rem;
  box-shadow: 0 8px 20px ${props => props.color}40;
  transition: all 0.3s ease;

  ${Card}:hover & {
    transform: scale(1.1) rotate(5deg);
    box-shadow: 0 12px 30px ${props => props.color}60;
  }

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
    margin-bottom: 1rem;
  }
`;

export const CardContent = styled.div`
  text-align: left;
`;

export const CardTitle = styled.h3`
  color: #2B3857;
  font-size: 1.4rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  text-shadow: 0 2px 4px rgba(43, 56, 87, 0.1);

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

export const CardDescription = styled.p`
  color: #64748B;
  font-size: 1rem;
  line-height: 1.5;
  margin: 0;

  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;
